"use client";

// OrbitDial — the tiny compass that rides inside the "orbit" chip when a folder
// holds enough work to wrap the full 360°. The faint ring is the whole circle of
// files; the lit wedge is the slice you're looking at right now. As you revolve
// the orbit the wedge sweeps around, so you always know where you are in the set
// and how much sits outside the view. At rest it caps the top (where the most
// recent work sits). Shown only when the orbit is actually revolvable.

const R = 8.4;
const C = 12;
const TAU = Math.PI * 2;

// a filled wedge from screen-angle φ1 → φ2 (0 = east, π/2 = up), sampled as a
// polygon so there's no SVG arc-flag guesswork. Screen y is up, SVG y is down.
function wedge(phi1: number, phi2: number, steps = 28): string {
  let d = `M ${C} ${C}`;
  for (let s = 0; s <= steps; s++) {
    const phi = phi1 + ((phi2 - phi1) * s) / steps;
    const x = C + R * Math.cos(phi);
    const y = C - R * Math.sin(phi);
    d += ` L ${x.toFixed(2)} ${y.toFixed(2)}`;
  }
  return d + " Z";
}

export default function OrbitDial({
  spin,
  windowArc,
  page = 0,
  pages = 1,
  size = 17,
}: {
  spin: number;
  windowArc: number;
  /** orbit-page position — the dial resets each revolution; "2/3" says where. */
  page?: number;
  pages?: number;
  size?: number;
}) {
  // the wedge reads the CURRENT PAGE's revolve — each completed revolution
  // starts the dial over (the pages of the circle), the counter carrying which
  // page of files is seated.
  const local = pages > 1 ? spin - Math.max(0, Math.min(pages - 1, Math.floor(spin / TAU + 1e-4))) * TAU : spin;
  const center = Math.PI / 2 - local;
  const half = Math.min(Math.PI - 0.05, windowArc / 2);
  const path = wedge(center + half, center - half);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", flexShrink: 0, verticalAlign: "middle" }}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        aria-hidden
        style={{ marginLeft: "0.5rem", flexShrink: 0, display: "inline-block" }}
      >
        <circle cx={C} cy={C} r={R} fill="none" stroke="currentColor" strokeOpacity={0.28} strokeWidth={1.6} />
        {/* the wedge takes the CHIP's own ink (currentColor), never the organism
            hue — the live dial must sit tonally inside its chip on any ground. */}
        <path d={path} fill="currentColor" fillOpacity={0.85} />
        <circle cx={C} cy={C} r={R} fill="none" stroke="currentColor" strokeOpacity={0.4} strokeWidth={1.3} />
      </svg>
      {pages > 1 && (
        <span
          aria-label={`orbit page ${page + 1} of ${pages}`}
          style={{ marginLeft: "0.3rem", fontSize: "0.66rem", opacity: 0.75, letterSpacing: "0.02em", fontVariantNumeric: "tabular-nums" }}
        >
          {page + 1}/{pages}
        </span>
      )}
    </span>
  );
}
